import React, { useMemo } from "react";
import { TransformControls } from "@react-three/drei";
import {
  ThreeEvent,
  createPortal,
  useFrame,
  useThree,
} from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import { TransformControls as TransformControlsImpl } from "three-stdlib";
import { editorStoreContext } from "../../../../../store/editorStore";
import { FloorplannerStoreContext } from "../../../../../store/floorplannerStore";
import {
  CircleStairsType,
  RectStairsType,
  TurnStairsType,
  SymbolType,
  WallType,
  isCircleStairsType,
  isRectStairsType,
  isTurnStairsType,
} from "../../../../../types/floorplannerTypes";
import { useSelectedSymbol3DDrag } from "../../../interaction/useSelectedSymbol3DDrag";
import type { Symbol3DDragVisualState } from "../../../interaction/useSelectedSymbol3DDrag";
import { getSymbol3DGroupTranslateOffset } from "../../../interaction/symbol3DGroupTranslate";
import {
  applyRotationGizmoSensitivity,
  createRotationGizmoSensitivitySession,
  getRotationGizmoControlSize,
  getSymbol3DRotationFromObject,
  isPointerOverTransformControlsPicker,
  registerTransformControlsPointerGuard,
  ROTATION_GIZMO_SNAP_STEP,
  syncSymbolPlanRotationAfter3DYawChange,
  type RotationGizmoSensitivitySession,
  type Symbol3DRotation,
} from "../../../interaction/symbol3DRotationGizmo";
import CatalogStandardMaterial from "../../../materials/CatalogStandardMaterial";
import {
  hasAssignedSlotMaterial,
  resolvePrimitiveSymbolPartMaterial,
  resolvePrimitiveSymbolPartSurfaceRef,
} from "../../../materials/primitiveSymbolFill";
import { useBuiltInMaterialSurfaceMappingsRevision } from "../../../materials/builtInMaterialSurfaceMappings";
import { resolveFillOpacity } from "../../../../../utils/fillOpacity";
import ExtrudedShapeMesh from "../../../scene/ExtrudedShapeMesh";
import { usePointerUpSelection } from "../../../scene/usePointerUpSelection";
import { getSymbol3DBaseElevation } from "../../openings/openingGeometry";
import { getSymbol3DDefinition } from "../../registry/symbol3DRegistry";
import {
  Stair3DStepDefinition,
  createCircularStair3DSteps,
  createRectStair3DSteps,
  createTurnStair3DSteps,
  getStairStepExtrusion,
  getTurnStairsPlanBounds,
} from "./stairGeometry";

const MIN_STAIR_GIZMO_HEIGHT = 0.05;

const StairPrimitive3DMeshes: React.FC<{
  symbols: SymbolType[];
  walls: WallType[];
  selectedSymbolIds: Set<string>;
  claimSelectionEvents?: boolean;
  onSelectSymbol: (symbol: SymbolType, event: ThreeEvent<PointerEvent>) => void;
  onDragActiveChange?: (active: boolean) => void;
}> = observer(
  ({
    symbols,
    walls,
    selectedSymbolIds,
    claimSelectionEvents = false,
    onSelectSymbol,
    onDragActiveChange,
  }) => {
    return (
      <>
        {symbols.map((symbol) => {
          const definition = getSymbol3DDefinition(symbol);
          if (definition.renderMode !== "primitive") return null;

          if (
            definition.kind === "circleStairs" &&
            isCircleStairsType(symbol)
          ) {
            return (
              <StairPrimitiveSymbol
                key={symbol.id}
                symbol={symbol}
                walls={walls}
                selected={selectedSymbolIds.has(symbol.id)}
                claimSelectionEvents={claimSelectionEvents}
                onSelectSymbol={onSelectSymbol}
                onDragActiveChange={onDragActiveChange}
              />
            );
          }

          if (definition.kind === "rectStairs" && isRectStairsType(symbol)) {
            return (
              <StairPrimitiveSymbol
                key={symbol.id}
                symbol={symbol}
                walls={walls}
                selected={selectedSymbolIds.has(symbol.id)}
                claimSelectionEvents={claimSelectionEvents}
                onSelectSymbol={onSelectSymbol}
                onDragActiveChange={onDragActiveChange}
              />
            );
          }

          if (definition.kind === "turnStairs" && isTurnStairsType(symbol)) {
            return (
              <StairPrimitiveSymbol
                key={symbol.id}
                symbol={symbol}
                walls={walls}
                selected={selectedSymbolIds.has(symbol.id)}
                claimSelectionEvents={claimSelectionEvents}
                onSelectSymbol={onSelectSymbol}
                onDragActiveChange={onDragActiveChange}
              />
            );
          }

          return null;
        })}
      </>
    );
  },
);

const StairPrimitiveSymbol: React.FC<{
  symbol: CircleStairsType | RectStairsType | TurnStairsType;
  walls: WallType[];
  selected: boolean;
  claimSelectionEvents?: boolean;
  onSelectSymbol: (symbol: SymbolType, event: ThreeEvent<PointerEvent>) => void;
  onDragActiveChange?: (active: boolean) => void;
}> = observer(
  ({
    symbol,
    walls,
    selected,
    claimSelectionEvents = false,
    onSelectSymbol,
    onDragActiveChange,
  }) => {
    const editorStore = React.useContext(editorStoreContext);
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const { camera, invalidate, scene } = useThree();
    const groupRef = React.useRef<THREE.Group | null>(null);
    const rotationWorldPositionRef = React.useRef(new THREE.Vector3());
    const transformControlsRef = React.useRef<TransformControlsImpl | null>(
      null,
    );
    const rotateTransactionRef = React.useRef(false);
    const rotateChangedRef = React.useRef(false);
    const rotationStart3DRef = React.useRef<Symbol3DRotation | undefined>(
      undefined,
    );
    const rotationSensitivityRef =
      React.useRef<RotationGizmoSensitivitySession | null>(null);
    const [isRotationDragging, setIsRotationDragging] = React.useState(false);
    const [rotationObject, setRotationObject] =
      React.useState<THREE.Group | null>(null);
    const selectionHandlers = usePointerUpSelection(symbol, onSelectSymbol, {
      claimPointerEvents: claimSelectionEvents,
    });
    const applyVisualState = React.useCallback(
      (object: THREE.Object3D, state: Symbol3DDragVisualState) => {
        const baseElevation = getFiniteBaseElevation(
          state.threeD ?? symbol.threeD,
        );
        object.position.set(state.position.x, state.position.y, baseElevation);
        object.updateMatrixWorld();
      },
      [symbol.threeD],
    );
    const dragHandlers = useSelectedSymbol3DDrag({
      symbol,
      selected,
      walls,
      onDragActiveChange,
      visualTargetRef: groupRef,
      applyVisualState,
    });
    const baseElevation = getSymbol3DBaseElevation(symbol);
    const steps = useMemo<Stair3DStepDefinition[]>(() => {
      const options = {
        defaultStepSize: floorplannerStore.stairStepSize,
      };
      if (isTurnStairsType(symbol)) {
        return createTurnStair3DSteps(symbol, options);
      }
      return isCircleStairsType(symbol)
        ? createCircularStair3DSteps(symbol, options)
        : createRectStair3DSteps(symbol, options);
    }, [
      floorplannerStore.stairStepSize,
      symbol,
      symbol.stairStepSize,
      isCircleStairsType(symbol) ? symbol.openAngle : undefined,
      isCircleStairsType(symbol) ? symbol.circleStairsWidth : undefined,
      isRectStairsType(symbol) ? symbol.openAngle : undefined,
      isRectStairsType(symbol) ? symbol.rectStairsWidth : undefined,
      isRectStairsType(symbol) ? symbol.rectStairsHeight : undefined,
      isTurnStairsType(symbol) ? symbol.stairShape : undefined,
      isTurnStairsType(symbol) ? symbol.turnDirection : undefined,
      isTurnStairsType(symbol) ? symbol.firstFlightSteps : undefined,
      isTurnStairsType(symbol) ? symbol.secondFlightSteps : undefined,
      isTurnStairsType(symbol) ? symbol.landingDepth : undefined,
      isTurnStairsType(symbol) ? symbol.stairWidth : undefined,
    ]);
    const stairMaterial = resolvePrimitiveSymbolPartMaterial(symbol, "stair");
    // Re-resolve when admin Surface overlays load/change.
    useBuiltInMaterialSurfaceMappingsRevision();
    const stairSurfaceRef = resolvePrimitiveSymbolPartSurfaceRef(
      symbol,
      "stair",
    );
    const stairSlotAssigned = hasAssignedSlotMaterial(symbol, "stair");
    const color = stairMaterial.color;
    const roughnessFace = stairMaterial.roughness ?? 0.74;
    const roughnessSide = stairMaterial.roughness ?? 0.82;
    const metalness = stairMaterial.metalness ?? 0;
    const stairPresetDefaults = stairMaterial.builtIn
      ? {
          roughness: stairMaterial.builtIn.roughness,
          metalness: stairMaterial.builtIn.metalness,
          repeatScale: stairMaterial.builtIn.repeatScale,
        }
      : undefined;
    const fillOpacity = resolveFillOpacity(symbol);
    const fillIsTransparent = fillOpacity < 1;
    /** Glass presets need MeshPhysicalMaterial transmission (same as shapes). */
    const stairIsGlass =
      stairSlotAssigned &&
      stairMaterial.isGlass &&
      Boolean(stairMaterial.builtIn);
    const flipX = symbol.flipHorizontal ? -1 : 1;
    const flipY = symbol.flipVertical ? -1 : 1;
    const rotation3D = symbol.threeD?.rotation3D;
    const rotationControlsVisible = Boolean(
      !editorStore.panning &&
        selected &&
        rotationObject &&
        editorStore.selections.length === 1 &&
        editorStore.selections[0]?.id === symbol.id,
    );
    const rotationControlDimensions = useMemo(
      () =>
        getStairRotationControlDimensions(
          symbol,
          steps,
          floorplannerStore.stairStepSize,
        ),
      [floorplannerStore.stairStepSize, symbol, steps],
    );
    const rotationControlSize = getRotationGizmoControlSize(
      rotationControlDimensions,
    );
    const setRotationGroupRef = React.useCallback(
      (group: THREE.Group | null) => {
        setRotationObject(group);
      },
      [],
    );
    React.useLayoutEffect(() => {
      if (!rotationControlsVisible || !transformControlsRef.current) return;
      return registerTransformControlsPointerGuard(
        transformControlsRef.current,
      );
    }, [rotationControlsVisible]);
    const beginRotation = React.useCallback(() => {
      if (!rotationObject || rotateTransactionRef.current) return;
      rotateTransactionRef.current = true;
      rotateChangedRef.current = false;
      setIsRotationDragging(true);
      rotationStart3DRef.current = floorplannerStore.symbolsMap.get(
        symbol.id,
      )?.threeD?.rotation3D;
      rotationSensitivityRef.current =
        createRotationGizmoSensitivitySession(rotationObject);
      floorplannerStore.beginUndoTransaction("rotate 3D stairs");
      floorplannerStore.recordSymbolForUndo(symbol.id);
      onDragActiveChange?.(true);
    }, [floorplannerStore, onDragActiveChange, rotationObject, symbol.id]);
    const updateRotation = React.useCallback(() => {
      if (!rotationObject) return;
      applyRotationGizmoSensitivity(rotationObject, rotationSensitivityRef.current, {
        snapStep: editorStore.isShiftPressed ? ROTATION_GIZMO_SNAP_STEP : null,
      });
      rotateChangedRef.current = true;
      invalidate();
    }, [editorStore.isShiftPressed, invalidate, rotationObject]);
    const finishRotation = React.useCallback(() => {
      if (!rotationObject || !rotateTransactionRef.current) return;
      rotateTransactionRef.current = false;
      setIsRotationDragging(false);
      rotationSensitivityRef.current = null;
      onDragActiveChange?.(false);

      if (!rotateChangedRef.current) {
        floorplannerStore.cancelUndoTransaction();
        invalidate();
        return;
      }

      const currentSymbol = floorplannerStore.symbolsMap.get(symbol.id);
      if (!currentSymbol) {
        floorplannerStore.cancelUndoTransaction();
        invalidate();
        return;
      }

      const nextRotation3D = getSymbol3DRotationFromObject(rotationObject);
      const { rotation, rotation3D } = syncSymbolPlanRotationAfter3DYawChange(
        currentSymbol.rotation,
        rotationStart3DRef.current,
        nextRotation3D,
      );
      floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
        ...currentSymbol.threeD,
        rotation3D,
      });
      floorplannerStore.updateSymbolProperty(symbol.id, "rotation", rotation);
      floorplannerStore.recordSymbolChange(symbol.id, "modify");
      floorplannerStore.pushToUndoStack();
      invalidate();
    }, [
      floorplannerStore,
      invalidate,
      onDragActiveChange,
      rotationObject,
      symbol.id,
    ]);
    React.useEffect(
      () => () => {
        if (rotateTransactionRef.current) {
          rotateTransactionRef.current = false;
          setIsRotationDragging(false);
          floorplannerStore.cancelUndoTransaction();
          onDragActiveChange?.(false);
        }
      },
      [floorplannerStore, onDragActiveChange],
    );
    const updateRotationControlSize = React.useCallback(
      (currentCamera: THREE.Camera) => {
        if (
          !rotationControlsVisible ||
          !rotationObject ||
          !transformControlsRef.current
        ) {
          return;
        }
        rotationObject.updateMatrixWorld(true);
        transformControlsRef.current.setSize(
          getRotationGizmoControlSize(rotationControlDimensions, {
            camera: currentCamera,
            worldPosition: rotationObject.getWorldPosition(
              rotationWorldPositionRef.current,
            ),
          }),
        );
      },
      [rotationControlDimensions, rotationControlsVisible, rotationObject],
    );
    React.useLayoutEffect(() => {
      updateRotationControlSize(camera);
    }, [camera, updateRotationControlSize]);
    useFrame(({ camera }) => {
      updateRotationControlSize(camera);
    });
    const pointerHandlers = useMemo(
      () => ({
        onPointerDown: (event: ThreeEvent<PointerEvent>) => {
          if (
            isPointerOverTransformControlsPicker(
              transformControlsRef.current,
              event,
            )
          ) {
            return;
          }
          dragHandlers.onPointerDown(event);
          selectionHandlers.onPointerDown(event);
        },
        onPointerMove: (event: ThreeEvent<PointerEvent>) => {
          dragHandlers.onPointerMove(event);
          selectionHandlers.onPointerMove(event);
        },
        onPointerUp: (event: ThreeEvent<PointerEvent>) => {
          dragHandlers.onPointerUp(event);
          selectionHandlers.onPointerUp(event);
        },
        onPointerCancel: (event: ThreeEvent<PointerEvent>) => {
          dragHandlers.onPointerCancel(event);
        },
      }),
      [dragHandlers, selectionHandlers],
    );

    const groupTranslateOffset = getSymbol3DGroupTranslateOffset(symbol.id);

    return (
      <>
        <group
          ref={groupRef}
          position={[
            symbol.position.x + groupTranslateOffset.x,
            symbol.position.y + groupTranslateOffset.y,
            baseElevation,
          ]}
          rotation={[0, 0, symbol.rotation ?? 0]}
          scale={[flipX, flipY, 1]}
        >
          <group
            ref={setRotationGroupRef}
            {...(!isRotationDragging && {
              rotation: [
                rotation3D?.pitch ?? 0,
                rotation3D?.roll ?? 0,
                rotation3D?.yaw ?? 0,
              ],
            })}
          >
            {steps.map((step, index) => {
              const previousHeight = index > 0 ? steps[index - 1].height : 0;
              const { depth, positionZ } = getStairStepExtrusion(
                step.height,
                previousHeight,
              );
              return (
                <ExtrudedShapeMesh
                  key={step.id}
                  shape={step.shape}
                  depth={depth}
                  positionZ={positionZ}
                  selected={selected}
                  {...pointerHandlers}
                  userData={{
                    floorplannerType: "symbol",
                    floorplannerId: symbol.id,
                    floorplannerSymbolKind: symbol.type,
                  }}
                >
                  {stairIsGlass && stairMaterial.builtIn ? (
                    <>
                      <meshPhysicalMaterial
                        attach="material-0"
                        color={color}
                        roughness={
                          stairSurfaceRef?.roughness ??
                          stairMaterial.builtIn.roughness
                        }
                        metalness={0}
                        transmission={stairMaterial.builtIn.transmission ?? 1}
                        thickness={stairMaterial.builtIn.thickness ?? 0.05}
                        ior={stairMaterial.builtIn.ior ?? 1.5}
                        transparent
                        opacity={fillOpacity < 1 ? fillOpacity : 1}
                        side={THREE.DoubleSide}
                        depthWrite={!fillIsTransparent}
                      />
                      <meshPhysicalMaterial
                        attach="material-1"
                        color={color}
                        roughness={
                          stairSurfaceRef?.roughness ??
                          stairMaterial.builtIn.roughness
                        }
                        metalness={0}
                        transmission={stairMaterial.builtIn.transmission ?? 1}
                        thickness={stairMaterial.builtIn.thickness ?? 0.05}
                        ior={stairMaterial.builtIn.ior ?? 1.5}
                        transparent
                        opacity={fillOpacity < 1 ? fillOpacity : 1}
                        side={THREE.DoubleSide}
                        depthWrite={!fillIsTransparent}
                      />
                    </>
                  ) : stairSlotAssigned ? (
                    <>
                      <CatalogStandardMaterial
                        attach="material-0"
                        color={color}
                        surfaceRef={stairSurfaceRef}
                        defaults={{
                          ...stairPresetDefaults,
                          roughness: roughnessFace,
                        }}
                        side={THREE.DoubleSide}
                        opacity={fillOpacity}
                        transparent={fillIsTransparent}
                        depthWrite={!fillIsTransparent}
                      />
                      <CatalogStandardMaterial
                        attach="material-1"
                        color={color}
                        surfaceRef={stairSurfaceRef}
                        defaults={{
                          ...stairPresetDefaults,
                          roughness: roughnessSide,
                        }}
                        side={THREE.DoubleSide}
                        opacity={fillOpacity}
                        transparent={fillIsTransparent}
                        depthWrite={!fillIsTransparent}
                      />
                    </>
                  ) : (
                    <>
                      <meshStandardMaterial
                        attach="material-0"
                        color={color}
                        roughness={roughnessFace}
                        metalness={metalness}
                        side={THREE.DoubleSide}
                        opacity={fillOpacity}
                        transparent={fillIsTransparent}
                        depthWrite={!fillIsTransparent}
                      />
                      <meshStandardMaterial
                        attach="material-1"
                        color={color}
                        roughness={roughnessSide}
                        metalness={metalness}
                        side={THREE.DoubleSide}
                        opacity={fillOpacity}
                        transparent={fillIsTransparent}
                        depthWrite={!fillIsTransparent}
                      />
                    </>
                  )}
                </ExtrudedShapeMesh>
              );
            })}
          </group>
        </group>
        {rotationControlsVisible &&
          rotationObject &&
          createPortal(
            <TransformControls
              ref={transformControlsRef}
              object={rotationObject}
              mode="rotate"
              space="local"
              rotationSnap={
                editorStore.isShiftPressed ? ROTATION_GIZMO_SNAP_STEP : null
              }
              size={rotationControlSize}
              onMouseDown={beginRotation}
              onObjectChange={updateRotation}
              onMouseUp={finishRotation}
            />,
            scene,
          )}
      </>
    );
  },
);

function getFiniteBaseElevation(threeD: SymbolType["threeD"]): number {
  const baseElevation = threeD?.baseElevation;
  return Number.isFinite(baseElevation) && typeof baseElevation === "number"
    ? baseElevation
    : 0;
}

function getStairRotationControlDimensions(
  symbol: CircleStairsType | RectStairsType | TurnStairsType,
  steps: readonly Stair3DStepDefinition[],
  defaultStepSize: number,
): { width?: number; depth?: number; height?: number } {
  const height = Math.max(
    MIN_STAIR_GIZMO_HEIGHT,
    ...steps.map((step) => step.height),
  );

  if (isTurnStairsType(symbol)) {
    const bounds = getTurnStairsPlanBounds(symbol, { defaultStepSize });
    return {
      width: bounds.width,
      depth: bounds.depth,
      height,
    };
  }

  if (isCircleStairsType(symbol)) {
    return {
      width: symbol.circleStairsWidth,
      depth: symbol.circleStairsWidth,
      height,
    };
  }

  return {
    width: symbol.rectStairsWidth,
    depth: symbol.rectStairsHeight,
    height,
  };
}

export default StairPrimitive3DMeshes;
